import { useCallback, useState } from 'react'
import { Grid } from 'semantic-ui-react'
import { FormikProvider, useFormik } from 'formik'
import {
  CREATE_SINGLE_STATE_INCOME_TAX_DETAILS_KEY,
  PersonalExemptionAppliedAs,
  StateIncomeTaxDetailsStates,
  StateIncomeTaxEstimateDetails,
  TaxRateType,
  createSingleStateIncomeTaxDetails,
} from '../stateIncomeTaxDetailsActions'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { DateTime } from 'luxon'
import {
  Alert,
  Button,
  FormikCheckbox,
  FormikDateInput,
  FormikDropdown,
  FormikInput,
  GridRowColumn,
  Modal,
  getFieldName,
  makeDateSchema,
  makeNumberSchema,
  makeReqStringSchema,
  Text,
} from '@/componentsV1/BaseComponents'
import { getCurrentUser } from '@/selectors/user.selectors'
import {
  FILING_STATUS_FIELDS,
  OPTIONAL_AMOUNT_SCHEMA,
  type FilingStatusKey,
} from '../../IncomeTaxDetailsHelpers'
import {
  TaxRateBracketsInput,
  bracketsToDraft,
  draftToBrackets,
  emptyBracketsDraft,
  isBracketsDraftValid,
} from './TaxRateBracketsInput'
import {
  centsToDollars,
  dollarsToCents,
  formatCurrencyFromCents,
} from '@/utils/currencyHelpers'
import { selectErrorsForKeys } from '@/reducers/fetch'
import { useAppDispatch } from '@/utils/typeHelpers'

interface Props {
  modalOpen: boolean
  existingDetails: StateIncomeTaxEstimateDetails[]
  onClose: (newDetails?: StateIncomeTaxEstimateDetails) => void
}

/**
 * Clearing a currency input leaves "0" rather than an empty string, so a blank
 * field and a deliberately-zeroed one are indistinguishable here. A state either
 * has a standard deduction for every filing status or has none at all, and a $0
 * deduction is arithmetically identical to no deduction — so treat zero as unset
 * and require the five to agree, rather than writing a $0 deduction for one
 * status while the others carry real amounts.
 */
const isAmountSet = (value: string) => value !== '' && dollarsToCents(value) > 0

const STANDARD_DEDUCTION_FIELD_NAMES = {
  single: 'standardDeductionSingle',
  headOfHousehold: 'standardDeductionHeadOfHousehold',
  marriedFilingSeparately: 'standardDeductionMarriedFilingSeparately',
  qualifyingWidow: 'standardDeductionQualifyingWidow',
  marriedFilingJointly: 'standardDeductionMarriedFilingJointly',
} as const satisfies Record<FilingStatusKey, string>

/**
 * Exemptions are counted per person, so the couple statuses carry two.
 * Confirmed with the tax team: head of household takes the single amount,
 * qualifying surviving spouse the couple amount.
 */
const EXEMPTION_COUNT_BY_FILING_STATUS: Record<FilingStatusKey, number> = {
  single: 1,
  headOfHousehold: 1,
  marriedFilingSeparately: 1,
  qualifyingWidow: 2,
  marriedFilingJointly: 2,
} as const

const dollarsField = (cents?: number | null) =>
  cents || cents === 0 ? String(centsToDollars(cents)) : ''

const toState = (value: unknown) =>
  Object.values(StateIncomeTaxDetailsStates).find((state) => state === value)

export const StateIncomeTaxDetailsModal = ({
  onClose,
  modalOpen,
  existingDetails,
}: Props) => {
  const dispatch = useAppDispatch()
  const error = useReselector(selectErrorsForKeys, [
    CREATE_SINGLE_STATE_INCOME_TAX_DETAILS_KEY,
  ])[0]
  const [submitting, setSubmitting] = useState(false)

  const currentUser = useReselector(getCurrentUser)

  const formik = useFormik({
    initialValues: {
      effectiveFrom: '',
      current: false,
      state: undefined as StateIncomeTaxDetailsStates | undefined,
      standardDeductionSingle: '',
      standardDeductionHeadOfHousehold: '',
      standardDeductionMarriedFilingSeparately: '',
      standardDeductionQualifyingWidow: '',
      standardDeductionMarriedFilingJointly: '',
      personalExemptionAppliedAs: undefined as
        | PersonalExemptionAppliedAs
        | undefined,
      personalExemptionAmount: '',
      taxRateType: undefined as TaxRateType | undefined,
      taxRateFlat: '',
      taxRateBrackets: emptyBracketsDraft(),
    },
    onSubmit: async ({
      effectiveFrom,
      current,
      state,
      standardDeductionSingle,
      standardDeductionHeadOfHousehold,
      standardDeductionMarriedFilingSeparately,
      standardDeductionQualifyingWidow,
      standardDeductionMarriedFilingJointly,
      personalExemptionAppliedAs,
      personalExemptionAmount,
      taxRateType,
      taxRateFlat,
      taxRateBrackets,
    }) => {
      if (!state || !taxRateType || !currentUser) {
        return
      }

      const standardDeductionEntered = [
        standardDeductionSingle,
        standardDeductionHeadOfHousehold,
        standardDeductionMarriedFilingSeparately,
        standardDeductionQualifyingWidow,
        standardDeductionMarriedFilingJointly,
      ].every(isAmountSet)

      const exemptionInCents = dollarsToCents(personalExemptionAmount)

      try {
        setSubmitting(true)
        const data = await createSingleStateIncomeTaxDetails({
          effectiveFrom: DateTime.fromFormat(
            effectiveFrom,
            DATE_FORMATS_LUXON.INPUT,
            { zone: 'utc' }
          ).toJSDate(),
          current,
          state,
          standardDeduction: standardDeductionEntered
            ? {
                single: dollarsToCents(standardDeductionSingle),
                headOfHousehold: dollarsToCents(
                  standardDeductionHeadOfHousehold
                ),
                marriedFilingSeparately: dollarsToCents(
                  standardDeductionMarriedFilingSeparately
                ),
                qualifyingWidow: dollarsToCents(
                  standardDeductionQualifyingWidow
                ),
                marriedFilingJointly: dollarsToCents(
                  standardDeductionMarriedFilingJointly
                ),
              }
            : undefined,
          personalExemption:
            personalExemptionAppliedAs && personalExemptionAmount !== ''
              ? {
                  appliedAs: personalExemptionAppliedAs,
                  single: exemptionInCents,
                  headOfHousehold: exemptionInCents,
                  marriedFilingSeparately: exemptionInCents,
                  qualifyingWidow: exemptionInCents * 2,
                  marriedFilingJointly: exemptionInCents * 2,
                }
              : undefined,
          taxRateType,
          taxRateFlat: Number(taxRateFlat),
          taxRateBrackets:
            taxRateType === TaxRateType.bracket
              ? draftToBrackets(taxRateBrackets)
              : undefined,
          updatedBy: currentUser.id,
        })(dispatch)
        setSubmitting(false)
        if (data) {
          onClose(data)
          formik.resetForm()
        }
      } catch (err) {
        console.error(err)
      }
    },
  })

  const { submitForm, values, setValues, setFieldValue } = formik

  /*
    Every write creates a whole new row rather than updating one, so anything not
    re-entered here is written as null. Seeding from the state's current row keeps
    a standard deduction from being silently dropped. Every field is replaced, not
    merged — carrying a previous selection's brackets or rate over to the next
    state would write one state's schedule onto another.
  */
  const prefillFromCurrentRow = useCallback(
    (state?: StateIncomeTaxDetailsStates) => {
      const currentRow = existingDetails.find(
        (detail) => detail.state === state && detail.current
      )
      setValues((previous) => ({
        ...previous,
        state,
        standardDeductionSingle: dollarsField(
          currentRow?.standardDeduction?.single
        ),
        standardDeductionHeadOfHousehold: dollarsField(
          currentRow?.standardDeduction?.headOfHousehold
        ),
        standardDeductionMarriedFilingSeparately: dollarsField(
          currentRow?.standardDeduction?.marriedFilingSeparately
        ),
        standardDeductionQualifyingWidow: dollarsField(
          currentRow?.standardDeduction?.qualifyingWidow
        ),
        standardDeductionMarriedFilingJointly: dollarsField(
          currentRow?.standardDeduction?.marriedFilingJointly
        ),
        personalExemptionAppliedAs: currentRow?.personalExemption?.appliedAs,
        personalExemptionAmount: dollarsField(
          currentRow?.personalExemption?.single
        ),
        taxRateType: currentRow?.taxRateType,
        taxRateFlat:
          currentRow?.taxRateFlat === undefined ||
          currentRow?.taxRateFlat === null
            ? ''
            : String(currentRow.taxRateFlat),
        taxRateBrackets: currentRow?.taxRateBrackets
          ? bracketsToDraft(currentRow.taxRateBrackets)
          : emptyBracketsDraft(),
      }))
    },
    [existingDetails, setValues]
  )

  const exemptionInCents = dollarsToCents(values.personalExemptionAmount)
  const bracketsValid =
    values.taxRateType !== TaxRateType.bracket ||
    isBracketsDraftValid(values.taxRateBrackets)
  const standardDeductionAmounts = FILING_STATUS_FIELDS.map(
    ({ key }) => values[STANDARD_DEDUCTION_FIELD_NAMES[key]]
  )
  const standardDeductionComplete =
    standardDeductionAmounts.every(isAmountSet) ||
    !standardDeductionAmounts.some(isAmountSet)

  return (
    <FormikProvider value={formik}>
      <Modal open={modalOpen} closeIcon onClose={() => onClose()}>
        <Modal.Header>Create new state income tax details</Modal.Header>
        <Modal.Content>
          <Grid>
            <GridRowColumn>
              {error && <Alert type="error" title={error.message} />}
            </GridRowColumn>
            <GridRowColumn>
              <FormikDropdown
                label="State"
                optionValues={Object.keys(StateIncomeTaxDetailsStates)}
                name={getFieldName<typeof values>('state')}
                schema={makeReqStringSchema()}
                onChange={(value) => prefillFromCurrentRow(toState(value))}
                required
              />
              <Text as="bodyXs">
                Selecting a state loads its current values below. Edit only what
                is changing — a new row is created, so anything cleared here is
                saved as empty.
              </Text>
            </GridRowColumn>
            <GridRowColumn>
              <FormikCheckbox
                label="Use in current QTE calculations"
                name={getFieldName<typeof values>('current')}
              />
            </GridRowColumn>
            <GridRowColumn>
              <FormikDateInput
                label="Effective From"
                name={getFieldName<typeof values>('effectiveFrom')}
                schema={makeDateSchema()}
                required
              />
            </GridRowColumn>
            <GridRowColumn>
              <Text as="h3">Standard Deduction</Text>
              <Text as="bodyXs">
                Entered in dollars. Leave all five blank for a state with no
                standard deduction.
              </Text>
            </GridRowColumn>
            {!standardDeductionComplete && (
              <GridRowColumn>
                <Text as="bodyXs" color="red">
                  Enter all five standard deduction amounts, or clear all five.
                  A state has a deduction for every filing status or none at
                  all.
                </Text>
              </GridRowColumn>
            )}
            {FILING_STATUS_FIELDS.map(({ key, label }) => (
              <GridRowColumn key={key}>
                <FormikInput
                  label={label}
                  name={getFieldName<typeof values>(
                    STANDARD_DEDUCTION_FIELD_NAMES[key]
                  )}
                  componentType="currency"
                  schema={OPTIONAL_AMOUNT_SCHEMA}
                />
              </GridRowColumn>
            ))}
            <GridRowColumn>
              <Text as="h3">Personal Exemption</Text>
              <Text as="bodyXs">
                Leave blank for a state with no personal exemption. Enter the
                amount for one exemption — the filing statuses below are derived
                from it.
              </Text>
            </GridRowColumn>
            <GridRowColumn>
              <FormikDropdown
                label="Applied As"
                placeholder="No personal exemption"
                clearable
                optionValues={Object.values(PersonalExemptionAppliedAs)}
                name={getFieldName<typeof values>('personalExemptionAppliedAs')}
              />
              <Text as="bodyXs">
                <b>deduction</b> subtracts from income before rates.{' '}
                <b>credit</b> subtracts from the tax owed, clamped at $0.
              </Text>
            </GridRowColumn>
            {values.personalExemptionAppliedAs && (
              <>
                <GridRowColumn>
                  <FormikInput
                    label="Amount per exemption"
                    name={getFieldName<typeof values>(
                      'personalExemptionAmount'
                    )}
                    componentType="currency"
                    schema={makeNumberSchema({ allowedDecimals: 2 })}
                    required
                  />
                </GridRowColumn>
                <GridRowColumn>
                  <Text as="bodyXs">Saved as:</Text>
                  {FILING_STATUS_FIELDS.map(({ key, label }) => (
                    <Text as="bodyXs" key={key}>
                      {label}:{' '}
                      {formatCurrencyFromCents(
                        exemptionInCents * EXEMPTION_COUNT_BY_FILING_STATUS[key]
                      )}
                    </Text>
                  ))}
                </GridRowColumn>
              </>
            )}
            <GridRowColumn>
              <FormikDropdown
                label="Tax Rate Type"
                optionValues={[TaxRateType.flat, TaxRateType.bracket]}
                name={getFieldName<typeof values>('taxRateType')}
                schema={makeReqStringSchema()}
                required
              />
            </GridRowColumn>
            {values.taxRateType === TaxRateType.flat && (
              <GridRowColumn>
                <FormikInput
                  label="Flat Tax Rate"
                  name={getFieldName<typeof values>('taxRateFlat')}
                  componentType="number"
                  schema={makeNumberSchema({
                    maxNumber: 1,
                    allowedDecimals: 4,
                  })}
                  required
                  prefix=""
                  decimalScale={4}
                />
              </GridRowColumn>
            )}
            {values.taxRateType === TaxRateType.bracket && (
              <TaxRateBracketsInput
                value={values.taxRateBrackets}
                onChange={(next) => setFieldValue('taxRateBrackets', next)}
              />
            )}
            {values.taxRateType === TaxRateType.bracket && !bracketsValid && (
              <GridRowColumn>
                <Text as="bodyXs" color="red">
                  Every band needs a rate between 0 and 1, and an upper bound
                  above the band before it. Only the last band may be left open.
                </Text>
              </GridRowColumn>
            )}
          </Grid>
        </Modal.Content>
        <Modal.Actions className="flex gap-3 justify-end">
          <Button variant="secondary" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            disabled={
              submitting ||
              !formik.isValid ||
              !bracketsValid ||
              !standardDeductionComplete
            }
            onClick={submitForm}
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </Modal.Actions>
      </Modal>
    </FormikProvider>
  )
}
